import {
  Category,
  CategoryPayload,
  createCategory,
  deleteCategory,
  getCategories,
  updateCategory,
} from "@/api/category-api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCollectionData } from "@/hooks/use-api";
import { toast } from "@/hooks/use-toast";
import { FolderTree, Pencil, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { mutate } from "swr";

const QUICK_ICONS = ["🥤", "☕", "🍜", "🍕", "🥐", "👗", "💄", "👜", "👟", "📱", "🛒", "🎁"];

interface CategoryManagerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCategoryCreated?: (category: Category) => void;
}

export function CategoryManagerModal({
  open,
  onOpenChange,
  onCategoryCreated,
}: CategoryManagerModalProps) {
  const { data: categoriesData, isLoading } = useCollectionData<Category[]>(
    open ? "/categories" : null,
    getCategories
  );
  const categories = categoriesData ?? [];

  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("");
  const [parentId, setParentId] = useState<string>("none");
  const [sortOrder, setSortOrder] = useState<string>("0");
  const [isSaving, setIsSaving] = useState(false);

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setIcon("");
    setParentId("none");
    setSortOrder("0");
  };

  const handleStartEdit = (cat: Category) => {
    setEditingId(String(cat.id));
    setName(cat.name);
    setIcon(cat.icon || "");
    setParentId(cat.parentId ? String(cat.parentId) : "none");
    setSortOrder(String(cat.sortOrder ?? 0));
  };

  const refreshCategoryCaches = async () => {
    await mutate(
      (key) =>
        typeof key === "string" &&
        (key.startsWith("/categories") ||
          key.startsWith("/pos/products") ||
          key.startsWith("/products")),
      undefined,
      { revalidate: true }
    );
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;

    setIsSaving(true);
    try {
      const payload: CategoryPayload = {
        name: trimmed,
        icon: icon.trim() || null,
        parentId: parentId && parentId !== "none" ? parentId : null,
        sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
        isActive: true,
      };

      if (editingId) {
        await updateCategory(editingId, payload);
        toast({
          title: "Category updated",
          description: `"${trimmed}" has been updated.`,
        });
      } else {
        const res = await createCategory(payload);
        toast({
          title: "Category created",
          description: `"${trimmed}" is now available for your products and Mini App.`,
        });
        if (res?.data && onCategoryCreated) {
          onCategoryCreated(res.data);
        }
      }

      resetForm();
      await refreshCategoryCaches();
    } catch (err: any) {
      toast({
        title: "Failed to save category",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (cat: Category) => {
    try {
      await deleteCategory(String(cat.id));
      toast({
        title: "Category deleted",
        description: `"${cat.name}" removed. Products in this category are now Uncategorized.`,
      });
      if (editingId === String(cat.id)) {
        resetForm();
      }
      await refreshCategoryCaches();
    } catch (err: any) {
      toast({
        title: "Failed to delete category",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    }
  };

  const parentCategories = categories.filter(
    (c) => !c.parentId && String(c.id) !== editingId
  );

  const getParentName = (pid?: string | null) => {
    if (!pid) return null;
    return categories.find((c) => String(c.id) === String(pid))?.name ?? null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderTree className="h-5 w-5 text-primary" />
            Manage Product Categories
          </DialogTitle>
          <DialogDescription>
            Create categories for your Storefront, Telegram Mini App, and POS. Products without a category stay under <strong>All / Uncategorized</strong>.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSave} className="space-y-3 border rounded-xl p-3.5 bg-slate-50/70">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              {editingId ? "Edit Category" : "New Category"}
            </span>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                <X className="h-3 w-3" /> Cancel edit
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5">
            <div className="sm:col-span-3 space-y-1">
              <Label htmlFor="cat-icon" className="text-xs">
                Icon (Optional)
              </Label>
              <Input
                id="cat-icon"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                placeholder="e.g. 🥤"
                className="h-9 text-center bg-white"
              />
            </div>

            <div className="sm:col-span-9 space-y-1">
              <Label htmlFor="cat-name" className="text-xs">
                Category Name *
              </Label>
              <Input
                id="cat-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Drinks / ភេសជ្ជៈ, Dresses, Mini Mart..."
                className="h-9 bg-white"
                required
              />
            </div>
          </div>

          {/* Quick Emoji Picker */}
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[11px] text-muted-foreground mr-1">Quick icon:</span>
            {QUICK_ICONS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => setIcon(icon === emoji ? "" : emoji)}
                className={`h-6 w-6 rounded text-xs flex items-center justify-center transition-colors ${
                  icon === emoji
                    ? "bg-primary/15 ring-1 ring-primary"
                    : "hover:bg-slate-200/70"
                }`}
              >
                {emoji}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5 pt-1">
            <div className="sm:col-span-8 space-y-1">
              <Label className="text-xs">Parent Category (Optional)</Label>
              <Select value={parentId} onValueChange={setParentId}>
                <SelectTrigger className="h-9 bg-white text-xs">
                  <SelectValue placeholder="None (Top-level category)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Top-level category)</SelectItem>
                  {parentCategories.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.icon ? `${p.icon} ` : ""}
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-4 space-y-1">
              <Label htmlFor="cat-sort" className="text-xs">
                Sort Order
              </Label>
              <Input
                id="cat-sort"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="h-9 bg-white text-xs"
              />
            </div>
          </div>

          <div className="flex justify-end pt-1">
            <Button type="submit" size="sm" disabled={isSaving || !name.trim()}>
              <Plus className="h-4 w-4 mr-1.5" />
              {isSaving
                ? "Saving..."
                : editingId
                  ? "Update Category"
                  : "Add Category"}
            </Button>
          </div>
        </form>

        {/* Category List */}
        <div className="flex-1 overflow-y-auto min-h-[160px] max-h-[280px] space-y-1.5 pr-1">
          {isLoading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              Loading categories...
            </div>
          ) : categories.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center border border-dashed rounded-lg">
              No categories yet. All products are currently displayed flat under <strong>All Menu</strong>.
            </div>
          ) : (
            categories.map((cat) => {
              const parentName = getParentName(cat.parentId);
              return (
                <div
                  key={cat.id}
                  className="flex items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2 text-sm hover:border-slate-300 transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {cat.icon && <span className="text-base">{cat.icon}</span>}
                    <div className="min-w-0">
                      <div className="font-medium truncate flex items-center gap-1.5">
                        {parentName && (
                          <span className="text-xs text-muted-foreground font-normal">
                            {parentName} /
                          </span>
                        )}
                        <span>{cat.name}</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {cat.productCount ?? 0} product
                        {(cat.productCount ?? 0) === 1 ? "" : "s"}
                        {cat.sortOrder ? ` • Order: ${cat.sortOrder}` : ""}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-slate-600"
                      onClick={() => handleStartEdit(cat)}
                      title="Edit category"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(cat)}
                      title="Delete category"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
